<template>
  <el-dialog
    v-model="innerVisible"
    title="用户隐私协议"
    width="600px"
    :close-on-click-modal="true"
    :close-on-press-escape="true"
    append-to-body
    destroy-on-close
  >
    <div class="privacy-content">
      <section class="privacy-section">
        <h3>一、信息收集</h3>
        <p>我们仅在您注册账号时收集必要的信息，包括：</p>
        <ul>
          <li><strong>手机号：</strong>用于账号登录、身份验证、找回密码等安全功能。</li>
          <li><strong>密码：</strong>经过加密后存储，用于保护您的账户安全。</li>
          <li><strong>昵称：</strong>可选，用于个性化展示。如未填写，将使用您的手机号作为昵称。</li>
        </ul>
        <p>除上述信息外，我们不会主动收集您的地理位置、通讯录、相册等与核心功能无关的个人信息。</p>
      </section>

      <section class="privacy-section">
        <h3>二、信息使用</h3>
        <p>您的信息仅用于以下目的：</p>
        <ul>
          <li>为您提供物流成本查询、网点管理等核心服务；</li>
          <li>保障账号安全，防止未经授权的访问；</li>
          <li>发送服务相关通知（如验证码、系统维护提示等）；</li>
          <li>在法律允许的范围内，优化我们的服务体验。</li>
        </ul>
      </section>

      <section class="privacy-section">
        <h3>三、信息存储与保护</h3>
        <p>我们采用行业标准的安全技术与管理措施保护您的个人信息：</p>
        <ul>
          <li>密码使用强加密算法（PBKDF2 + SHA256）存储，无法还原明文；</li>
          <li>数据传输使用 HTTPS 协议加密；</li>
          <li>服务器部署于国内合规数据中心，具备访问控制与日志审计机制。</li>
        </ul>
      </section>

      <section class="privacy-section">
        <h3>四、信息共享</h3>
        <p>未经您的明确同意，我们<strong>不会</strong>将您的个人信息共享、转让或披露给任何第三方，但以下情况除外：</p>
        <ul>
          <li>法律法规要求或政府主管部门依法要求；</li>
          <li>为维护您或其他用户的合法权益所必需；</li>
          <li>与经您授权的合作物流服务商共享必要的查询信息（不含手机号等账户标识）。</li>
        </ul>
      </section>

      <section class="privacy-section">
        <h3>五、您的权利</h3>
        <p>您对自己的个人信息享有以下权利：</p>
        <ul>
          <li><strong>查询与修改：</strong>登录后可在个人中心查看和修改昵称等基本信息；</li>
          <li><strong>密码重置：</strong>可通过手机号验证码重置登录密码；</li>
          <li><strong>账号注销：</strong>如需注销账号，请联系客服。注销后我们将在 15 个工作日内删除或匿名化处理您的所有个人信息；</li>
          <li><strong>撤回同意：</strong>您可以随时停止使用我们的服务来撤回对本协议的同意。</li>
        </ul>
      </section>

      <section class="privacy-section">
        <h3>六、Cookie 使用</h3>
        <p>我们仅使用必要的 Cookie 来维持登录状态和保障安全验证，不会通过 Cookie 收集额外的个人信息。您可以通过浏览器设置禁用 Cookie，但这可能影响部分功能的正常使用。</p>
      </section>

      <section class="privacy-section">
        <h3>七、未成年人保护</h3>
        <p>本服务不面向未满 18 周岁的未成年人。如果您是未成年人，请在监护人的陪同下阅读本协议，并在取得监护人同意后使用我们的服务。</p>
      </section>

      <section class="privacy-section">
        <h3>八、协议更新</h3>
        <p>我们可能根据业务发展和法律法规的变化适时更新本隐私协议。更新后的协议将在本页面公布，重大变更时会通过站内通知或短信方式告知您。继续使用本服务即视为您同意受更新后协议的约束。</p>
      </section>

      <section class="privacy-section">
        <h3>九、联系我们</h3>
        <p>如果您对本隐私协议有任何疑问、意见或建议，可通过以下方式联系我们：</p>
        <ul>
          <li>客服邮箱：<a href="mailto:support@wuliu178.com">support@wuliu178.com</a></li>
          <li>服务时间：工作日 09:00 – 18:00</li>
        </ul>
      </section>
    </div>

    <template #footer>
      <el-button type="primary" @click="handleAgree">我已阅读并同意</el-button>
      <el-button @click="handleClose">关闭</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'agree'])

// 内部真正驱动 el-dialog 的响应式变量
const innerVisible = ref(props.modelValue)

// props -> innerVisible
watch(() => props.modelValue, (v) => {
  innerVisible.value = v
})

// innerVisible -> emit
watch(innerVisible, (v) => {
  emit('update:modelValue', v)
})

const handleClose = () => {
  innerVisible.value = false
}

const handleAgree = () => {
  innerVisible.value = false
  emit('agree')
}
</script>

<style scoped>
.privacy-content {
  max-height: 55vh;
  overflow-y: auto;
  padding-right: 8px;
  font-size: 14px;
  line-height: 1.8;
  color: #303133;
}

.privacy-section {
  margin-bottom: 20px;
}

.privacy-section h3 {
  font-size: 15px;
  font-weight: 600;
  color: #1f2d3d;
  margin: 0 0 10px 0;
  padding-bottom: 6px;
  border-bottom: 1px solid #ebeef5;
}

.privacy-section p {
  margin: 6px 0;
  color: #4b5563;
}

.privacy-section ul {
  margin: 6px 0 6px 18px;
  padding: 0;
}

.privacy-section ul li {
  margin-bottom: 4px;
  color: #4b5563;
}

.privacy-section a {
  color: #409eff;
  text-decoration: none;
}

.privacy-section a:hover {
  text-decoration: underline;
}
</style>
